import { attach, createEvent, createStore, sample } from 'effector';
import { io, type Socket } from 'socket.io-client';
import { LabmateSdkParams } from './types';
import { sleep } from '../sleep';

export interface LabmateServerResponse<T = unknown> {
  requestId?: string;
  data: T;
  message?: string;
  error?: string;
  [key: string]: unknown;
}

export interface LabmateServerEvent<T = unknown> {
  eventName: string;
  data: T;
  [key: string]: unknown;
}

export interface LabmateStatusEvent {
  message?: string;
  [key: string]: unknown;
}

export type LabmateCommandParams = Record<string, unknown>;

export interface LabmateCommandRequest<
  TParams extends LabmateCommandParams = LabmateCommandParams,
> {
  eventName: string;
  params?: TParams;
  requestId: string;
}

enum SocketState {
  idle = 'idle',
  connected = 'connected',
  reconnecting = 'reconnecting',
}

export const createLabmateSdk = (url: string) => {
  const socket: Socket = io(url, {
    autoConnect: true,
    withCredentials: false,
    transports: ['websocket', 'polling'],
    path: "/socket.io"
  });

  const $connectedChannels = createStore<number[]>([]);
  const pendingRequests = new Map<
    string,
    {
      resolve: (value: LabmateServerResponse<unknown>) => void;
      reject: (reason?: unknown) => void;
    }
  >();

  const setState = createEvent<SocketState>();
  const $state = createStore(SocketState.idle).on(setState, (_, state) => state);
  const $connected = $state.map((state) => state === SocketState.connected);

  const onConnectedState = sample({
    clock: $connected,
    filter: Boolean,
  });

  const rawConnected = createEvent<LabmateStatusEvent>();
  const rawDisconnected = createEvent<LabmateStatusEvent>();
  const rawResponse = createEvent<LabmateServerResponse>();
  const rawError = createEvent<LabmateServerResponse>();
  const rawEvent = createEvent<LabmateServerEvent>();

  socket.on('connect', () => {
    setState(SocketState.connected);
    rawConnected({ message: 'Socket connected' });
  });

  socket.on('disconnect', (reason: unknown) => {
    setState(SocketState.reconnecting);
    rawDisconnected({ message: String(reason) });
  });

  socket.on('connected', (data: LabmateStatusEvent) => {
    setState(SocketState.connected);
    rawConnected(data);
  });

  socket.on('disconnected', (data: LabmateStatusEvent) => {
    setState(SocketState.reconnecting);
    rawDisconnected(data);
  });

  socket.on('response', (data: LabmateServerResponse) => {
    rawResponse(data);

    if (data.requestId) {
      const pending = pendingRequests.get(data.requestId);
      if (pending) {
        pendingRequests.delete(data.requestId);
        pending.resolve(data);
      }
    }
  });

  socket.on('error', (data: LabmateServerResponse) => {
    rawError(data);

    if (data.requestId) {
      const pending = pendingRequests.get(data.requestId);
      if (pending) {
        pendingRequests.delete(data.requestId);
        pending.reject(data);
      }
    }
  });

  socket.on('event', (data: LabmateServerEvent) => {
    rawEvent(data);
  });

  let requestIdCounter = 0;

  const nextRequestId = () => `req-${++requestIdCounter}-${Date.now()}`;

  /**
   * Отправка команды на сервер в стиле исходного Socket.IO‑кода:
   *   socket.emit('command', { eventName, params, requestId })
   *
   * Возвращает промис, который резолвится, когда приходит 'response' с тем же requestId
   * и реджектится, когда приходит 'error' с тем же requestId.
   */
  const sendCommand = <
    TParams extends LabmateCommandParams = LabmateCommandParams,
    TResponse = unknown,
  >(
    eventName: string,
    params?: TParams,
    requestId = nextRequestId(),
  ): Promise<LabmateServerResponse<TResponse>> => {
    const payload: LabmateCommandRequest<TParams> = {
      eventName,
      params,
      requestId,
    };

    const promise = new Promise<LabmateServerResponse<TResponse>>((resolve, reject) => {
      pendingRequests.set(requestId, {
        resolve: resolve as (value: LabmateServerResponse<unknown>) => void,
        reject,
      });

      socket.emit('command', payload);
    });

    return promise;
  };

  /**
   * Низкоуровневый emit для произвольных Socket.IO‑событий.
   */
  const emitRaw = <T>(eventName: string, data: T) => {
    socket.emit(eventName, data);
  };

  /**
   * Generic‑подписка на любое Socket.IO‑событие, в стиле effector:
   *   const onMyEvent = sdk.listen<MyType>('my-event')
   *   onMyEvent.watch(...)
   */
  const listen = <T>(eventName: string) => {
    const event = createEvent<T>();
    socket.on(eventName, (data: T) => {
      event(data);
    });
    return event;
  };

  /**
   * Высокоуровневые события, где наружу отдаются только payload'ы.
   * Для response/error — это поле data, как в твоём исходном коде.
   */
  const onResponse = sample({
    clock: rawResponse,
    fn: ({ data }) => data,
  });

  const onError = sample({
    clock: rawError,
    fn: ({ data }) => data,
  });

  const onEvent = <T>(eventName: string) => {
    const event = createEvent<T>();
    socket.on('event', (payload: LabmateServerEvent) => {
      if (payload.eventName === eventName) {
        event(payload.data as T);
      }
    });
    return event;
  }

  const connect = () => socket.connect();
  const disconnect = () => socket.disconnect();

  const getSensorsListFx = attach({
    source: $connected,
    effect: async (connected) => {
      if (!connected) {
        console.info('[labmate] socket is not connected to labmate');
        return;
      }

      const isAlive = await sendCommand<{}, { success: boolean }>('sendHelloCMD');

      console.log('[labmate] sendStatus', isAlive.data);

      if (isAlive.data.success) {
        const devices = await sendCommand<{}, { sensors: Array<{ channelNumber: number; subChannelNumber: number; userSensorId: string; }> }>('getConnectedSensorList');

        return devices.data.sensors;
      }

      return;
    },
  });

  const connectToSensorFx = attach({
    source: $connected,
    effect: async (connected, params: LabmateSdkParams.ConnectToSensorParams) => {
      if (!connected) {
        console.info('[labmate] socket is not connected to labmate');
        return;
      }

      const isAlive = await sendCommand<{}, { success: boolean }>('sendHelloCMD');


      console.log('[labmate] connectToSensorFx', isAlive.data);

      const connectionParams = {
        ...params,
        durationSeconds: 1000000,
        samplesPerSecond: params.frequency,
      }

      console.log('[labmate] connectToSensorFx runDemo', connectionParams);
      const connect = await sendCommand('runDemo', {
        ...connectionParams,
      });

      console.log('[labmate] connect', connect);
    },
  });

  const getBluetoothDevicesFx = attach({
    source: $connected,
    effect: async (connected) => {
      if (!connected) {
        console.info('[labmate] socket is not connected to labmate');
        return [];
      }

      const isAlive = await sendCommand<{}, { success: boolean }>('sendStatus');

      if (!isAlive.data.success) {
        await connectToBluetoothFx({
          deviceName: 'LMII00232',
        })
      }

      // Wait 5 seconds for the device to be connected
      for (let i = 0; i < 10; i++) {
        const isAlive = await sendCommand<{}, { success: boolean }>('sendStatus');

        if (isAlive.data.success) {
          break;
        }

        await sleep(500);
      }

      return getSensorsListFx()
    },
  });

  const connectToBluetoothFx = attach({
    source: $connected,
    effect: async (connected, params: LabmateSdkParams.ConnectToBluetoothParams) => {
      if (!connected) {
        console.info('[labmate] socket is not connected to labmate');
        return;
      }

      await sendCommand('connectToBleDevice', params);
    },
  });

  const disconnectFromSensorFx = attach({
    source: $connected,
    effect: async (connected) => {
      if (!connected) {
        console.info('[labmate] socket is not connected to labmate');
        return;
      }

      await sendCommand('sendStop');
      await sleep(2000);
    },
  });

  const changeFrequencyFx = attach({
    source: {
      connected: $connected,
      connectedChannels: $connectedChannels,
    },
    effect: async ({ connected, connectedChannels }, frequency: number) => {
      if (!connected) {
        console.info('[labmate] socket is not connected to labmate');
        return;
      }

      if (connectedChannels.length === 0) {
        console.info('[labmate] no channels connected');
        return;
      }

      await disconnectFromSensorFx();
      await connectToSensorFx({
        channelNumbers: connectedChannels,
        frequency,
      });
    },
  });


  $connectedChannels
    .on(connectToSensorFx.done, (_, data) => data.params.channelNumbers)
    .reset(disconnectFromSensorFx.doneData)

  return {
    socket,

    // состояние соединения
    $state,
    $connected,
    onConnectedState,

    // статусные события
    rawConnected,
    rawDisconnected,

    // ответы / ошибки / доменные события
    rawResponse,
    rawError,
    rawEvent,
    onResponse,
    onError,
    onEvent,

    // команды и низкоуровневый emit
    sendCommand,
    emitRaw,

    // generic‑подписка на любые события
    listen,

    // управление соединением
    connect,
    disconnect,

    getSensorsListFx,
    connectToSensorFx,
    getBluetoothDevicesFx,
    connectToBluetoothFx,
    disconnectFromSensorFx: disconnectFromSensorFx,
    changeFrequencyFx,
  };
};

export type LabmateSdk = ReturnType<typeof createLabmateSdk>;

